import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  ShieldAlert,
  CalendarX,
  HardHat,
  CalendarClock,
} from "lucide-react";
import { fetcher } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct, fmtDec, fmtCompact } from "@/lib/format";
import { Breadcrumb } from "@/components/Shared";
import { PersonAvatar } from "@/components/PersonAvatar";
import { SemaforoBadge, TrafficLight } from "@/components/Semaforo";
import { MetricCard } from "@/components/MetricCard";
import { TrendChart } from "@/components/TrendChart";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const META = {
  ventas: { title: "Ventas", fmt: fmtCompact, key: "ventas" },
  adr: { title: "ADR", fmt: (v) => fmtMoney(v), key: "adr" },
  comisiones: { title: "Comisiones y descuentos", fmt: (v) => fmtMoney(v), key: "comisiones" },
  inventarios: { title: "Inventarios", fmt: (v) => `${fmtDec(v, 0)}%`, key: "inventarios" },
  taller: { title: "Taller", fmt: (v) => fmtInt(v), key: "taller" },
  presupuesto: { title: "Presupuesto planta", fmt: (v) => fmtMoney(v), key: "presupuesto" },
};

function SafetyCard({ icon: Icon, label, value, big }) {
  return (
    <div className="flex-1 min-w-[180px] flex items-center gap-3 px-5 py-4 border-r border-white/20 last:border-r-0">
      <Icon className="w-6 h-6 text-white/80 shrink-0" strokeWidth={1.8} />
      <div>
        <div className="text-[11px] uppercase tracking-wide text-white/80 font-semibold leading-tight">
          {label}
        </div>
        <div className={`font-display font-black text-white ${big ? "text-3xl" : "text-xl"} leading-none mt-1`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, sub }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className="font-display font-black text-xl text-slate-900 leading-tight">{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function VentasDetail({ m }) {
  const v = m.ventas || {};
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryStat label="Tendencia" value={fmtInt(v.tendencia_kilos)} sub="kilos" />
        <SummaryStat label="Acumulado" value={fmtInt(v.acumulado)} sub="kilos al corte" />
        <SummaryStat label="Mes anterior" value={fmtInt(v.mes_anterior)} sub="kilos" />
        <SummaryStat label="Año anterior" value={fmtInt(v.ano_anterior)} sub="kilos" />
      </div>
      {v.daily && v.daily.length > 0 ? (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-100 hover:bg-slate-100">
                <TableHead className="text-xs uppercase tracking-wider">Día</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Portátil</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Autotanque</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Carburación</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Terceros</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right font-bold">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {v.daily.map((d) => (
                <TableRow key={d.dia} className="border-b border-slate-100">
                  <TableCell className="font-semibold">{d.dia}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtDec(d.portatil)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtDec(d.autotanque)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtDec(d.carburacion)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtDec(d.terceros)}</TableCell>
                  <TableCell className="text-right tabular-nums font-bold text-[#E2333F]">{fmtDec(d.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <NoDetail />
      )}
    </div>
  );
}

function AdrDetail({ m }) {
  const canales = m.adr?.canales || [];
  if (!canales.length) return <BigChart trend={m.adr?.trend} fmt={(v) => fmtMoney(v)} note="Utilidad de gestión por kilo (tendencia mensual)." />;
  return (
    <div className="space-y-6">
      {canales.map((c) => (
        <div key={c.canal}>
          <div className="flex items-center justify-between mb-2">
            <span className="bg-[#E2333F] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm">{c.canal}</span>
            <span className="text-sm text-slate-500">
              Utilidad {fmtMoney(c.total_utilidad)} · {fmtMoney(c.utilidad_por_kilo, 3)} /kg · {fmtInt(c.total_kilos)} kg
            </span>
          </div>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <TableHead className="text-xs uppercase tracking-wider">POS / Nombre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Venta kilos</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Utilidad $</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">HG %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.pos.map((p, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="font-medium max-w-[280px] truncate">{p.nombre}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtDec(p.venta_kilos)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${p.utilidad < 0 ? "text-red-600" : "text-slate-900"}`}>{fmtMoney(p.utilidad)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#E2333F] font-semibold">{fmtDec(p.hg_pct)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}

function ComisionesDetail({ m }) {
  const canales = m.comisiones?.canales || [];
  if (!canales.length) return <BigChart trend={m.comisiones?.trend} fmt={(v) => fmtMoney(v)} note="Comisiones y descuentos por kilo (tendencia mensual)." />;
  return (
    <div className="space-y-6">
      {canales.map((c) => (
        <div key={c.canal}>
          <div className="flex items-center justify-between mb-2">
            <span className="bg-[#E2333F] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm">{c.canal}</span>
            <span className="text-sm text-slate-500">
              Venta {fmtMoney(c.total_venta)} · C/D {fmtMoney(c.total_comision)}
            </span>
          </div>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <TableHead className="text-xs uppercase tracking-wider">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Venta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Comisión / Desc.</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">¢K</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.clientes.map((cl, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="font-medium max-w-[300px] truncate">{cl.cliente}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtDec(cl.venta)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-[#E2333F]">{fmtDec(cl.comision)}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-500">{fmtDec(cl.ck, 4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}

function BigChart({ trend, fmt, note }) {
  return (
    <div>
      <TrendChart data={trend || []} height={300} formatter={fmt} />
      {note && <p className="text-sm text-slate-400 mt-3">{note}</p>}
    </div>
  );
}

function NoDetail() {
  return (
    <p className="text-sm text-slate-400 py-6 text-center">
      Detalle granular disponible próximamente para esta empresa. Consulta la tendencia mensual en la tarjeta.
    </p>
  );
}

function MetricDetail({ metricKey, metrics }) {
  if (metricKey === "ventas") return <VentasDetail m={metrics} />;
  if (metricKey === "adr") return <AdrDetail m={metrics} />;
  if (metricKey === "comisiones") return <ComisionesDetail m={metrics} />;
  if (metricKey === "inventarios")
    return <BigChart trend={metrics.inventarios?.trend} fmt={(v) => `${fmtDec(v, 0)}%`} note="HG Neto de inventario (tendencia mensual)." />;
  if (metricKey === "taller")
    return <BigChart trend={metrics.taller?.trend} fmt={(v) => fmtInt(v)} note="Unidades en taller (tendencia mensual)." />;
  if (metricKey === "presupuesto")
    return <BigChart trend={metrics.presupuesto?.trend} fmt={(v) => fmtMoney(v)} note="Costo de planta por kilo (tendencia mensual)." />;
  return null;
}

export default function CompanyDashboard() {
  const { id } = useParams();
  const [open, setOpen] = React.useState(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["company", id],
    queryFn: () => fetcher(`/company/${encodeURIComponent(id)}`),
  });

  if (isLoading) return <div className="text-slate-400 text-sm">Cargando empresa…</div>;
  if (isError || !data) return <div className="text-slate-500 text-sm">No se encontró la empresa.</div>;

  const { metrics, breadcrumb } = data;
  const safety = metrics.safety || {};

  const order = ["ventas", "adr", "comisiones", "inventarios", "taller", "presupuesto"];

  return (
    <div className="zae-fade max-w-[1400px] mx-auto">
      <Breadcrumb
        items={[
          { label: "Grupo Tomza", to: "/" },
          { label: breadcrumb.division, to: `/division/${breadcrumb.division_id}` },
          { label: breadcrumb.zona, to: `/zona/${encodeURIComponent(breadcrumb.zona_id)}` },
          { label: breadcrumb.regional, to: `/regional/${encodeURIComponent(breadcrumb.regional_id)}` },
          { label: data.name },
        ]}
      />

      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-4">
          <PersonAvatar name={data.gerente} size="lg" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E2333F] font-bold mb-1">
              {breadcrumb.division} · {breadcrumb.zona}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-slate-900">
                {data.name}
              </h1>
              <SemaforoBadge status={data.status} />
              {metrics.is_real && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm">
                  Datos reales · {data.period}
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm mt-1">
              Gerente: {data.gerente || "Sin asignar"} · Regional {breadcrumb.regional}
            </p>
          </div>
        </div>
        <div className="hidden md:flex"><TrafficLight status={data.status} /></div>
      </div>

      {/* Safety banner */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="flex flex-wrap bg-[#E2333F] rounded-sm shadow-sm overflow-hidden" data-testid="safety-vehicular">
          <SafetyCard icon={ShieldAlert} label="Días sin accidentes vehiculares" value={fmtInt(safety.dias_sin_accidentes_vehiculares)} big />
          <SafetyCard icon={CalendarX} label="Último accidente vehicular" value={safety.fecha_ultimo_accidente_vehicular || "-"} />
        </div>
        <div className="flex flex-wrap bg-[#E2333F] rounded-sm shadow-sm overflow-hidden" data-testid="safety-laboral">
          <SafetyCard icon={HardHat} label="Días sin accidentes laborales" value={fmtInt(safety.dias_sin_accidentes_laborales)} big />
          <SafetyCard icon={CalendarClock} label="Último accidente laboral" value={safety.fecha_ultimo_accidente_laboral || "-"} />
        </div>
      </div>

      {/* Metric grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {order.map((k) => {
          const meta = META[k];
          const block = metrics[k] || {};
          return (
            <MetricCard
              key={k}
              title={meta.title}
              label={block.label}
              trend={block.trend || []}
              formatter={meta.fmt}
              onClick={() => setOpen(k)}
              testId={`metric-card-${k}`}
            />
          );
        })}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="metric-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <span className="bg-[#E2333F] text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
                {open && META[open].title}
              </span>
              <span className="text-slate-400 text-sm font-medium">{data.name}</span>
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Detalle de {open && META[open].title} · {data.period}
              {!metrics.is_real && " · datos representativos"}
            </DialogDescription>
          </DialogHeader>
          {open && <MetricDetail metricKey={open} metrics={metrics} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
